import { z } from "zod";

// ---------------------------------------------------------------------------
// Enrollment States
// ---------------------------------------------------------------------------

export const ENROLLMENT_STATES = [
  "idle",
  "executing",
  "waiting",        // A1/A2/A3: enrollment paused waiting for event/datetime/condition
  "completed",
  "errored",
  "cancelled",
  "filtered_out",   // Sprint 1 A3: filter node halts enrollment
] as const;

export type EnrollmentState = (typeof ENROLLMENT_STATES)[number];

const TERMINAL_STATES: ReadonlySet<EnrollmentState> = new Set([
  "completed",
  "errored",
  "cancelled",
  "filtered_out",
]);

export function isTerminalState(state: EnrollmentState | string): boolean {
  return TERMINAL_STATES.has(state as EnrollmentState);
}

// ---------------------------------------------------------------------------
// Failure Policy
// ---------------------------------------------------------------------------

export const FAILURE_ACTIONS = ["retry", "skip", "abort", "goto"] as const;
export type FailureAction = (typeof FAILURE_ACTIONS)[number];

export const failurePolicySchema = z.object({
  action: z.enum(FAILURE_ACTIONS),
  maxRetries: z.number().int().min(0).max(10).default(3),
  retryDelayMs: z.number().int().min(1_000).max(3_600_000).default(30_000),
  timeoutMs: z.number().int().min(5_000).max(300_000).default(30_000),
  gotoNodeId: z.string().optional(),
});

export type FailurePolicy = z.infer<typeof failurePolicySchema>;

export const DEFAULT_FAILURE_POLICY: FailurePolicy = {
  action: "retry",
  maxRetries: 3,
  retryDelayMs: 30_000,
  timeoutMs: 30_000,
};

// ---------------------------------------------------------------------------
// Consent & Reentry
// ---------------------------------------------------------------------------

export const CONSENT_REQUIREMENTS = [
  "none",
  "marketing",
  "transactional",
  "custom",
] as const;

export type ConsentRequirement = (typeof CONSENT_REQUIREMENTS)[number];

export const REENTRY_POLICIES = [
  "once_ever",
  "after_completion",
  "allow_multiple",
] as const;

export type ReentryPolicy = (typeof REENTRY_POLICIES)[number];

// ---------------------------------------------------------------------------
// Quiet Hours & Frequency Cap
// ---------------------------------------------------------------------------

const timeStringSchema = z.string().regex(/^\d{2}:\d{2}$/).refine((val) => {
  const [h, m] = val.split(":").map(Number);
  return (h ?? -1) >= 0 && (h ?? -1) <= 23 && (m ?? -1) >= 0 && (m ?? -1) <= 59;
}, "Invalid time — hours must be 0-23, minutes 0-59");

export const quietHoursSchema = z.object({
  enabled: z.boolean(),
  start: timeStringSchema,
  end: timeStringSchema,
  timezoneSource: z.string(),
  fallbackTimezone: z.string(),
});

export type QuietHours = z.infer<typeof quietHoursSchema>;

export const frequencyCapSchema = z.object({
  maxMessages: z.number().int().min(1),
  periodHours: z.number().int().min(1),
  appliesTo: z.enum(["marketing", "transactional", "all"]),
});

export type FrequencyCap = z.infer<typeof frequencyCapSchema>;

// ---------------------------------------------------------------------------
// Workflow Status
// ---------------------------------------------------------------------------

export const WORKFLOW_STATUSES = [
  "draft",
  "active",
  "paused",
  "archived",
] as const;

export type WorkflowStatus = (typeof WORKFLOW_STATUSES)[number];

// ---------------------------------------------------------------------------
// Trigger Config
// ---------------------------------------------------------------------------

export const triggerConfigSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("event"), eventType: z.string().min(1), filter: z.record(z.unknown()).optional() }),
  z.object({ type: z.literal("segment_entry"), segmentId: z.string().uuid() }),
  z.object({ type: z.literal("schedule"), schedule: z.string().min(1) }),
  z.object({ type: z.literal("manual") }),
  z.object({ type: z.literal("api") }),
]);

export type TriggerConfig = z.infer<typeof triggerConfigSchema>;

// ---------------------------------------------------------------------------
// Workflow Definition
// ---------------------------------------------------------------------------

export interface WorkflowNode {
  id: string;
  type: string;
  actionRef: string;
  config: Record<string, unknown>;
  failurePolicy?: FailurePolicy;
  position?: { x: number; y: number };
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  condition?: string;
}

export interface WorkflowDefinition {
  id: string;
  tenantId: string;
  name: string;
  version: number;
  status: WorkflowStatus;
  trigger: TriggerConfig;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  reentryPolicy: ReentryPolicy;
  consentRequirements: ConsentRequirement[];
  quietHours?: QuietHours;
  frequencyCap?: FrequencyCap;
  rolloutPercent?: number;  // 0-100, default 100. For AI-generated: starts at 1.
  createdAt: Date;
  updatedAt: Date;
}

// ---------------------------------------------------------------------------
// Workflow Definition Zod Schema (validates at system boundaries — KV reads)
// ---------------------------------------------------------------------------

const workflowNodeSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  actionRef: z.string(),
  config: z.record(z.unknown()),
  failurePolicy: failurePolicySchema.optional(),
  position: z.object({ x: z.number(), y: z.number() }).optional(),
});

const workflowEdgeSchema = z.object({
  id: z.string().min(1),
  source: z.string().min(1),
  target: z.string().min(1),
  label: z.string().optional(),
  condition: z.string().optional(),
});

export const workflowDefinitionSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().min(1),
  name: z.string().min(1),
  version: z.number().int().min(1),
  status: z.enum(WORKFLOW_STATUSES),
  trigger: triggerConfigSchema,
  nodes: z.array(workflowNodeSchema).min(1),
  edges: z.array(workflowEdgeSchema),
  reentryPolicy: z.enum(REENTRY_POLICIES),
  consentRequirements: z.array(z.enum(CONSENT_REQUIREMENTS)),
  quietHours: quietHoursSchema.optional(),
  frequencyCap: frequencyCapSchema.optional(),
  rolloutPercent: z.number().int().min(0).max(100).optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// ---------------------------------------------------------------------------
// Alarm Interval Limits
// ---------------------------------------------------------------------------

/** Cloudflare DO max alarm interval. Alarms beyond this silently fail. Long waits use re-alarm chaining. */
export const MAX_ALARM_INTERVAL_MS = 25 * 86_400_000; // 25 days (safety margin below CF's ~30d limit)

// ---------------------------------------------------------------------------
// Enrollment DO State (lives in Durable Object SQLite)
// ---------------------------------------------------------------------------

export interface EnrollmentDOState {
  enrollmentId: string;
  doId: string;
  tenantId: string;
  workflowId: string;
  workflowVersion: number;
  contactId: string;
  currentNodeId: string | null;
  state: EnrollmentState;
  stepRetryCount: number;
  executionContext: Record<string, unknown>;
  causationChain: string[];
  correlationId: string;
  messagesSent: number;
  messagesWindow: number;
  createdAt: number;
  lastTransitionAt: number;
  completedAt: number | null;
  errorMessage: string | null;
  triggerEventId: string;
  alarmGeneration: number;
  waitForEventType?: string;      // A1: Event type the enrollment is waiting for
  waitForCondition?: string;      // A3: Serialized FilterTree condition (JSON)
  waitTimeoutAt?: number;         // A1: Unix ms when the wait times out (alarm fires)
  waitCheckIntervalMs?: number;   // A3: Re-evaluation interval for condition waits
}

// ---------------------------------------------------------------------------
// Enrollment Checkpoint (for DO alarm-based persistence)
// ---------------------------------------------------------------------------

export interface EnrollmentCheckpoint {
  enrollmentId: string;
  state: EnrollmentState;
  currentNodeId: string | null;
  stepRetryCount: number;
  executionContext: Record<string, unknown>;
  causationChain: string[];
  messagesSent: number;
  messagesWindow: number;
  lastTransitionAt: number;
  completedAt: number | null;
  errorMessage: string | null;
  alarmGeneration: number;
}

// ---------------------------------------------------------------------------
// Action Dispatch & Result
// ---------------------------------------------------------------------------

export interface ActionDispatch {
  enrollmentId: string;
  tenantId: string;
  workflowId: string;
  nodeId: string;
  actionRef: string;
  contactId: string;
  config: Record<string, unknown>;
  correlationId: string;
  causationId: string;
  callbackUrl: string;
  timeoutMs: number;
  // Monotonic attempt number for this (enrollment, node) pair.
  // Set by the state machine — used by step-logs writer to keep retry rows
  // distinct (append-only audit log, never overwrite).
  attempt: number;
}

export interface ActionResult {
  enrollmentId: string;
  success: boolean;
  output?: Record<string, unknown>;
  error?: string;
  retryable?: boolean;
}

// ---------------------------------------------------------------------------
// Create Enrollment Input
// ---------------------------------------------------------------------------

export interface CreateEnrollmentInput {
  tenantId: string;
  workflowId: string;
  workflowVersion: number;
  contactId: string;
  triggerEventId: string;
  correlationId: string;
  causationId: string | null;
  reentryPolicy: ReentryPolicy;
  executionContext?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Deterministic DO ID Builder
// ---------------------------------------------------------------------------

/**
 * Builds a deterministic Durable Object ID for a workflow enrollment.
 *
 * - once_ever / after_completion: `{tenantId}:{workflowId}:{contactId}`
 *   This ensures only one DO per contact per workflow, preventing duplicates.
 *
 * - allow_multiple: `{tenantId}:{workflowId}:{contactId}:{triggerEventId}`
 *   Each trigger event creates a separate enrollment.
 */
export function buildEnrollmentDOId(input: {
  tenantId: string;
  workflowId: string;
  contactId: string;
  reentryPolicy: ReentryPolicy;
  triggerEventId: string;
}): string {
  for (const [name, value] of [
    ["tenantId", input.tenantId],
    ["workflowId", input.workflowId],
    ["contactId", input.contactId],
    ["triggerEventId", input.triggerEventId],
  ] as const) {
    if (value.includes(":")) {
      throw new Error(`${name} must not contain colons: ${value}`);
    }
  }

  const base = `${input.tenantId}:${input.workflowId}:${input.contactId}`;

  if (input.reentryPolicy === "allow_multiple") {
    return `${base}:${input.triggerEventId}`;
  }

  return base;
}
